import { TaskStatus } from '@dadohr/shared/domainTypes/experience';
import {
  IReportingQueryDrilldown,
  IReportTaskInfoResolved
} from '@dadohr/shared/domainTypes/reports';
import { ONE_DAY } from '@dadohr/shared/domainTypes/time';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  IconButton,
  TableContainer,
  Tooltip,
  Typography
} from '@material-ui/core';
import { compact, flatten, groupBy, mapValues, sum, uniq } from 'lodash';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { WithShape } from '../../../../../components/Charts/Util';
import { getColor } from '../../../../../components/ColoredChip';
import {
  DataTable2Cell,
  DataTable2With2StickyColumns
} from '../../../../../components/DataTable';
import { toPercent } from '../../../../../components/Number';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { useUnsafeTranslation } from '../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../services/currentUser';
import { useExperienceTemplateById } from '../../../../../services/experiences/template';
import { useCombineLoadingValues } from '../../../../../services/firebase';
import { toFullName } from '../../../../../services/user';
import {
  getRelativeAnchorDefinition,
  useAnchorFields
} from '../../../../Experiences/components/Anchors';
import { CardItemDialog } from '../../../../Experiences/components/CalendarV2/CardItemDialog';
import { TimelineItemExecutionTableLazy } from '../../../../Experiences/components/CalendarV2/DialogTable';
import { getTaskStatusChipConfig } from '../../../../Experiences/components/TaskStatusChip';
import { getItemDisplayName, getItemDisplayPair } from '../../../../Experiences/helpers/adminName';
import { ParticipantDialogLazy } from '../../../../Experiences/pages/Details/ParticipantDialog';
import { useReportsDrilldownDataByInstance } from '../../../services/data';
import { ReportDrilldownSortKeys } from '../types';
import {
  AnchorTile,
  ANCHOR_GAP_MOCK_ID_PREFIX,
  BlankTile,
  collectGapsAndSort,
  CompletionPercentageChip,
  DataTable2RowOtherData,
  gapOrAnchorOrOther,
  LEFT_COL_WIDTH,
  NameContainer,
  ReportDrilldownEmptyState,
  ReportDrilldownError,
  ReportDrilldownLoader,
  RowData,
  shortName,
  SORTERS,
  SpacerTile,
  StatusTile,
  taskStatusToString,
  Tile,
  TrackingFns,
  useApplySearchAndSortRows,
  WithTooltipIfTruncated
} from './common';

type StatusCounts = { [key in TaskStatus]: number };

type AggregatedStatusType =
  | 'n/a'
  | 'Upcoming'
  | 'Skipped'
  | 'Past Deadline'
  | 'Green'
  | 'Yellow'
  | 'Red'
  | 'Other';

const getAggregatedStatus = (c: StatusCounts | null): AggregatedStatusType => {
  const total = sum(Object.values(c || {}));
  if (!c) {
    return 'n/a';
  }
  const areAll = (status: TaskStatus) => c[status] === total;
  if (areAll(TaskStatus.NOT_STARTED)) {
    return 'Upcoming';
  }
  if (areAll(TaskStatus.SKIPPED)) {
    return 'Skipped';
  }
  if (areAll(TaskStatus.PAST_DEADLINE)) {
    return 'Past Deadline';
  }

  const complete = c[TaskStatus.COMPLETE] || 0;
  const late = c[TaskStatus.LATE] || 0;

  const totalLateOrComplete = complete + late;
  const percentComplete = toPercent(complete, totalLateOrComplete);

  if (percentComplete >= 0.75) {
    return 'Green';
  }
  if (percentComplete >= 0.5) {
    return 'Yellow';
  }
  if (percentComplete < 0.5 && late) {
    return 'Red';
  }

  return 'Other';
};

const AggregateStatusTileContainer = styled(Tile)<{
  type: AggregatedStatusType;
}>((p) => {
  const { colors } = p.theme.custom;
  const getColor = () => {
    switch (p.type) {
      case 'n/a':
        return colors.gray.w100;
      case 'Upcoming':
        return colors.gray.w200;
      case 'Skipped':
        return colors.gray.w300;
      case 'Past Deadline':
        return colors.gray.w400;
      case 'Green':
        return colors.green.w300;
      case 'Yellow':
        return colors.yellow.w300;
      case 'Red':
        return colors.red.w300;
      default:
        return colors.gray.w200;
    }
  };

  return {
    backgroundColor: getColor()
  };
});

const AggregateStatusTooltip = ({
  statuses,
  info
}: {
  statuses: TaskStatus[];
  info: IReportTaskInfoResolved;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const groupedStatuses = mapValues(
    groupBy(statuses, (s) => s),
    (x) => x.length
  );
  return (
    <FlexContainerVertical>
      <div>
        {(() => {
          const { primary, secondary } = getItemDisplayPair(info);
          return (
            <>
              <div><strong>{primary}</strong></div>
              {secondary && <div><em>"{secondary}"</em></div>}
            </>
          );
        })()}
        <Typography variant="caption" color="inherit">
          <FlexContainer spacing={0.5}>
            <div>
              {info.dependentOn.length
                ? `${t('After completion of')}: ${info.dependentOn
                    .map((x) => getItemDisplayName(x))
                    .join(' & ')}`
                : `${t('Starts on day {{ count }}', {
                    count: Math.floor(info.typicalStart / ONE_DAY) + 1
                  })}`}
            </div>
          </FlexContainer>
        </Typography>
      </div>
      <FlexContainerVertical spacing={0.5} fullWidth>
        {[
          TaskStatus.COMPLETE,
          TaskStatus.SKIPPED,
          TaskStatus.RUNNING,
          TaskStatus.NOT_STARTED,
          TaskStatus.PAST_DEADLINE,
          TaskStatus.LATE
        ].map((s) => {
          const count = groupedStatuses[s];
          if (!count) {
            return null;
          }
          const [label, c] = getTaskStatusChipConfig(s, t);
          return (
            <WithShape key={s} shape="square" color={getColor(theme, c)[1]}>
              {label} ({count})
            </WithShape>
          );
        })}
      </FlexContainerVertical>
    </FlexContainerVertical>
  );
};

const AggregateStatusTile = ({
  statuses,
  info,
  onClick,
  onHover
}: {
  statuses: TaskStatus[];
  info: IReportTaskInfoResolved;
  onClick?: (
    info: IReportTaskInfoResolved,
    status: AggregatedStatusType
  ) => void;
  onHover?: (
    info: IReportTaskInfoResolved,
    status: AggregatedStatusType
  ) => void;
}) => {
  const counts = countStatuses(statuses);
  const aggStatus = getAggregatedStatus(counts);
  const _onClick = onClick ? () => onClick(info, aggStatus) : undefined;
  const _onHover = onHover ? () => onHover(info, aggStatus) : undefined;
  return (
    <Tooltip
      classes={{ tooltip: css({ '&&': { maxWidth: 'none' } }) }}
      title={<AggregateStatusTooltip info={info} statuses={statuses} />}
    >
      <AggregateStatusTileContainer
        type={aggStatus}
        onClick={_onClick}
        onMouseEnter={_onHover}
      />
    </Tooltip>
  );
};

const countStatuses = (statuses: TaskStatus[]): StatusCounts | null => {
  if (!statuses.length) {
    return null;
  }
  return mapValues(
    groupBy(statuses, (s) => s),
    (x) => x.length
  ) as StatusCounts;
};

export const BodyByActor = ({
  q,
  search,
  sort,
  trackingFns
}: {
  q: IReportingQueryDrilldown;
  search: string;
  sort: ReportDrilldownSortKeys;
  trackingFns: TrackingFns;
}) => {
  const type = q.otherData?.type || ''; // always defined
  const { org } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const [vs, loading, error] = useCombineLoadingValues(
    useExperienceTemplateById(q.tplIds[0]),
    useReportsDrilldownDataByInstance(q)
  );
  const [tpl, d] = vs || [undefined, undefined];
  const [expanded, setExpanded] = useState<{ [userId: string]: boolean }>({});

  const anchorFields = useAnchorFields(org.customUserFields);
  // we just pass primitives down into the hook, to avoid
  // having a dependency on the template itself, which might change for
  // unrelated reasons
  const anchorDefinition =
    (tpl && getRelativeAnchorDefinition(tpl.data.anchor, anchorFields, t)) ||
    null;
  const anchorPosition = anchorDefinition?.position ?? null;
  const anchorName = anchorDefinition?.shortLabel ?? null;
  const tplId = tpl?.id || '';

  const [
    peopleDialogTask,
    setPeopleDialogTask
  ] = useState<null | IReportTaskInfoResolved>(null);

  // Add a lazy dialog instance somehow
  const [dialogInstance, setDialogInstance] = useState<null | {
    tplId: string;
    instanceId: string;
  }>(null);
  const allRows = useMemo<null | RowData[]>(() => {
    if (!d || d.type !== 'actor') {
      return null;
    }
    const anchor =
      anchorPosition !== null && anchorName !== null
        ? { name: anchorName, position: anchorPosition }
        : null;
    const sortedTasks = collectGapsAndSort(
      Object.values(d.data.tasks),
      anchor,
      t
    );

    const expTitle = tpl?.data.title || '';
    const resolveToken = (s: string) =>
      s.replace(/\{\{\s*experience\.title\s*\}\}/g, expTitle);
    const resolvedTasksById: {
      [key: string]: IReportTaskInfoResolved;
    } = mapValues(d.data.tasks, (tsk) => {
      return {
        ...tsk,
        title: resolveToken(tsk.title),
        adminName: tsk.adminName != null ? resolveToken(tsk.adminName) : tsk.adminName,
        actor: d.data.actor
      };
    });

    const sorter =
      SORTERS[sort] || SORTERS[ReportDrilldownSortKeys.COMPLETION_RATE_ASC];

    return flatten(
      compact(
        d.data.containers.map<RowData[] | null>((c) => {
          const user = d.data.users[c.userId];
          if (!user) {
            // should never happen
            return null;
          }
          const otherData: DataTable2RowOtherData = {
            searchData: { name: toFullName(user) },
            sortData: {
              completionPercentage: c.topLevelRow.total.total
                ? c.topLevelRow.total.percentage
                : null,
              preferredName: user?.preferredName || '',
              lastName: user?.lastName || ''
            }
          };
          const exp = !!expanded[c.userId];
          const userShortName = shortName(user, 40);
          const userFullname = toFullName(user);
          const firstRowCells: DataTable2Cell[] = compact([
            {
              cell: (
                <NameContainer
                  spacing={1}
                  role="button"
                  onClick={() => {
                    if (!exp) {
                      trackingFns.trackRowExpanded(tplId, userFullname);
                    }
                    setExpanded((x) => ({ ...x, [c.userId]: !exp }));
                  }}
                >
                  {exp && <ChevronDown size={16} />}
                  {!exp && <ChevronRight size={16} />}
                  <div>
                    <WithTooltipIfTruncated
                      short={userShortName}
                      full={userFullname}
                    />
                  </div>
                </NameContainer>
              )
            },
            {
              cell: <CompletionPercentageChip v={c.topLevelRow.total} />
            },
            ...(exp
              ? sortedTasks.map((tsk) => ({
                  cell: gapOrAnchorOrOther(tsk, () => <BlankTile />)
                }))
              : sortedTasks.map((tsk) => {
                  const cell = gapOrAnchorOrOther(tsk, () => {
                    const statuses = compact(
                      c.instances.map((inst) => inst.byTask[tsk.id]?.status)
                    );
                    const resolvedTask = resolvedTasksById[tsk.id];
                    if (!resolvedTask) {
                      // should never happen
                      return <BlankTile />;
                    }
                    return (
                      <AggregateStatusTile
                        statuses={statuses}
                        info={resolvedTask}
                        onClick={(_, aggStatus) => {
                          trackingFns.trackBlockClick(
                            resolvedTask,
                            aggStatus,
                            'aggregatedStatus'
                          );
                          trackingFns.openExecutionDialog(resolvedTask);
                          setPeopleDialogTask(resolvedTask);
                        }}
                        onHover={(_, aggStatus) =>
                          trackingFns.trackBlockHover(
                            resolvedTask,
                            aggStatus,
                            'aggregatedStatus'
                          )
                        }
                      />
                    );
                  });
                  return { cell };
                }))
          ]);

          const firstRow: RowData = {
            key: c.userId,
            cells: firstRowCells,
            otherData
          };

          return [
            firstRow,
            ...(exp
              ? compact([
                  // THESE INSTANCES SHOULD ALSO BE SORTED VIA THE GENERAL SORT OPTIONS
                  ...sorter(
                    compact(
                      c.instances.map<{
                        row: RowData;
                        otherData: DataTable2RowOtherData;
                      } | null>((inst) => {
                        // The default should never be needed because the backend contains
                        // fallbacks already - but the demo content seems to have some holes.
                        const participant = d.data.users[
                          inst.participantId
                        ] || {
                          id: inst.participantId,
                          preferredName: 'Unknown',
                          lastName: 'User'
                        };
                        const pUserNameShort = shortName(participant, 34);
                        const pUserNameFull = toFullName(participant);
                        const cells: DataTable2Cell[] = [
                          {
                            cell: (
                              <NameContainer spacing={1} role="button">
                                <ChevronRight size={16} color="white" />
                                <FlexContainer spacing={0.5}>
                                  <span>
                                    <span>{t('for')} </span>
                                    <WithTooltipIfTruncated
                                      short={pUserNameShort}
                                      full={pUserNameFull}
                                    />
                                  </span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      trackingFns.openInstanceDialog(
                                        inst.instanceId
                                      );
                                      setDialogInstance({
                                        tplId: inst.templateId,
                                        instanceId: inst.instanceId
                                      });
                                    }}
                                  >
                                    <ExternalLink size={12} />
                                  </IconButton>
                                </FlexContainer>
                              </NameContainer>
                            )
                          },
                          {
                            cell: (
                              <CompletionPercentageChip
                                v={inst.total}
                                invertColors
                              />
                            )
                          },
                          ...sortedTasks.map((tsk) => {
                            const cell = gapOrAnchorOrOther(tsk, () => {
                              const task = resolvedTasksById[tsk.id];
                              const status =
                                inst.byTask[tsk.id]?.status || null;
                              return (
                                <StatusTile
                                  key={tsk.id}
                                  status={status}
                                  info={task}
                                  onClick={() => {
                                    trackingFns.trackBlockClick(
                                      task,
                                      taskStatusToString(status, t),
                                      'status'
                                    );
                                    trackingFns.openExecutionDialog(task);
                                    setPeopleDialogTask(task);
                                  }}
                                  hideActor
                                  onMouseEnter={() =>
                                    trackingFns.trackBlockHover(
                                      task,
                                      taskStatusToString(status, t),
                                      'status'
                                    )
                                  }
                                />
                              );
                            });
                            return { cell };
                          })
                        ];
                        return {
                          row: {
                            key: inst.instanceId,
                            cells,
                            otherData
                          },
                          otherData: {
                            sortData: {
                              completionPercentage:
                                inst.total.percentage || null,
                              preferredName: participant.preferredName,
                              lastName: participant.lastName
                            },
                            searchData: {
                              name: pUserNameFull // not really needed
                            }
                          }
                        };
                      })
                    )
                  ).map((x) => x.row),
                  // This is an empty row to provide a bit more spacing when being expanded
                  {
                    key: `${c.userId}-spacer`,
                    cells: [
                      { cell: <SpacerTile /> },
                      { cell: <SpacerTile /> },
                      ...sortedTasks.map((x) => {
                        if (x.id.startsWith(ANCHOR_GAP_MOCK_ID_PREFIX)) {
                          return { cell: <AnchorTile info={x} isSpacer /> };
                        }
                        return { cell: <SpacerTile /> };
                      })
                    ],
                    otherData
                  }
                ])
              : [])
          ];
        })
      )
    );
  }, [d, expanded, t, anchorPosition, anchorName, sort, tplId, trackingFns, tpl?.data.title]);

  const rows = useApplySearchAndSortRows(allRows, search, sort);

  useErrorLogger(`drilldown report - ${type}: error`, error);

  if (d && d.type !== 'actor') {
    return null;
  }
  return (
    <>
      <div>
        {loading && <ReportDrilldownLoader />}
        {!loading && rows && !!rows.length && (
          <TableContainer>
            <DataTable2With2StickyColumns
              caption={'Completion for all tasks table'}
              sticky={{
                paddingRight: 1,
                firstColumnWidth: LEFT_COL_WIDTH
              }}
              rows={rows}
            />
          </TableContainer>
        )}
        {!loading && rows && !rows.length && <ReportDrilldownEmptyState />}
        {!loading && !rows && error && <ReportDrilldownError />}
      </div>

      {peopleDialogTask && d && (
        <CardItemDialog
          open={true}
          onClose={() => setPeopleDialogTask(null)}
          headerLeft={
            <Typography variant="h4">{getItemDisplayName(peopleDialogTask)}</Typography>
          }
          dialogTable={
            <TimelineItemExecutionTableLazy
              type="task"
              // Controversial - what should be shown here?
              instanceIds={uniq(
                flatten(
                  d.data.containers.map((c) =>
                    c.instances.map((i) => i.instanceId)
                  )
                )
              )}
              tplId={peopleDialogTask.tplId}
              actor={peopleDialogTask.actor}
              itemTplId={peopleDialogTask.id}
              onShowInstanceDetails={(inst) =>
                setDialogInstance({
                  tplId: inst.data.templateId,
                  instanceId: inst.id
                })
              }
            />
          }
        />
      )}
      {dialogInstance && (
        <ParticipantDialogLazy
          open={true}
          onClose={() => setDialogInstance(null)}
          tplId={dialogInstance.tplId}
          instanceId={dialogInstance.instanceId}
        />
      )}
    </>
  );
};
